import { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { defaultProfileConfig } from '@/mocks/adminRadioProfile';
import type { RadioProfileConfig, ProfileSocial } from '@/mocks/adminRadioProfile';
import { SwalAlert } from '@/hooks/useSweetAlert';
import AdminImageUpload from '@/components/base/AdminImageUpload';

export default function AdminProfilePage() {
  const [config, setConfig] = useState<RadioProfileConfig>(defaultProfileConfig);
  const [saving, setSaving] = useState(false);
  const logoBlobRef = useRef<string | null>(null);
  const coverBlobRef = useRef<string | null>(null);

  const updateConfig = useCallback((patch: Partial<RadioProfileConfig>) => {
    setConfig((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleLogoChange = useCallback((file: File) => {
    if (logoBlobRef.current) URL.revokeObjectURL(logoBlobRef.current);
    const blobUrl = URL.createObjectURL(file);
    logoBlobRef.current = blobUrl;
    updateConfig({ logo: blobUrl });
    SwalAlert.toast('Logo cargado correctamente.', 'success');
  }, [updateConfig]);

  const handleLogoRemove = useCallback(() => {
    if (logoBlobRef.current) URL.revokeObjectURL(logoBlobRef.current);
    logoBlobRef.current = null;
    updateConfig({ logo: '' });
  }, [updateConfig]);

  const handleCoverChange = useCallback((file: File) => {
    if (coverBlobRef.current) URL.revokeObjectURL(coverBlobRef.current);
    const blobUrl = URL.createObjectURL(file);
    coverBlobRef.current = blobUrl;
    updateConfig({ cover: blobUrl });
    SwalAlert.toast('Portada cargada correctamente.', 'success');
  }, [updateConfig]);

  const handleCoverRemove = useCallback(() => {
    if (coverBlobRef.current) URL.revokeObjectURL(coverBlobRef.current);
    coverBlobRef.current = null;
    updateConfig({ cover: '' });
  }, [updateConfig]);

  const updateSocial = useCallback((id: string, field: keyof ProfileSocial, value: string | boolean) => {
    setConfig((prev) => ({
      ...prev,
      socials: prev.socials.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    }));
  }, []);

  const handleSave = useCallback(() => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      SwalAlert.toast('Perfil guardado correctamente', 'success');
    }, 800);
  }, []);

  const activeSocials = config.socials.filter((s) => s.active && s.url);

  return (
    <>
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-user-star-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Perfil Radio</h1>
              <p className="text-foreground-400 text-[9px]">Vista previa + configuración</p>
            </div>
          </div>
          <Link to="/admin/dashboard" className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors">
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
        </div>
      </header>


          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Perfil de la Radio</h2>
              <p className="text-foreground-400 text-xs">Configurá cómo se ve tu radio en el perfil público</p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>

          {/* Body — two columns */}
          <div className="flex flex-col lg:flex-row gap-6 p-4 md:p-6 lg:p-8 pb-8 max-w-[1440px]">
            {/* ─── LEFT: Config Panel ─── */}
            <div className="flex-1 min-w-0 space-y-6">
              {/* LOGO */}
              <section className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-image-2-line text-foreground-500 text-xs"></i>
                  Logo
                </h4>
                <AdminImageUpload
                  label="Logo de la Radio"
                  currentImage={config.logo}
                  onChange={handleLogoChange}
                  onRemove={handleLogoRemove}
                  maxSizeMB={2}
                  recommendedDimensions="512×512px"
                  previewHeight="h-24"
                  previewWidth="w-24"
                  placeholderIcon="ri-image-line"
                />
              </section>

              {/* PORTADA */}
              <section className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-image-line text-foreground-500 text-xs"></i>
                  Portada
                </h4>
                <AdminImageUpload
                  label="Imagen de Portada"
                  currentImage={config.cover}
                  onChange={handleCoverChange}
                  onRemove={handleCoverRemove}
                  recommendedDimensions="1600×400px"
                  previewHeight="h-36"
                  placeholderIcon="ri-image-line"
                />
              </section>

              {/* DESCRIPCIÓN */}
              <section className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-file-text-line text-foreground-500 text-xs"></i>
                  Descripción
                </h4>
                <textarea
                  value={config.description}
                  onChange={(e) => updateConfig({ description: e.target.value })}
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors resize-none leading-relaxed"
                  placeholder="Describí tu radio, lo que transmitís, tu estilo..."
                ></textarea>
                <p className="text-foreground-300 text-[9px] text-right mt-1">{config.description.length}/500</p>
              </section>

              {/* NOMBRE, CATEGORÍA, IDIOMA — Radio Profile */}
              <section className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-information-line text-foreground-500 text-xs"></i>
                  Datos de la Radio
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Nombre de la radio</label>
                    <input
                      type="text"
                      value={config.radioName || ''}
                      onChange={(e) => updateConfig({ radioName: e.target.value })}
                      placeholder="Radio Nueva Actitud"
                      className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Categoría</label>
                    <select
                      value={config.category || ''}
                      onChange={(e) => updateConfig({ category: e.target.value })}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors cursor-pointer appearance-none"
                    >
                      <option value="">Seleccionar categoría...</option>
                      {['Música', 'Noticias', 'Deportes', 'Entretenimiento', 'Cultural', 'Religiosa', 'Comunitaria', 'Variada'].map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Idioma</label>
                    <select
                      value={config.language || 'Español'}
                      onChange={(e) => updateConfig({ language: e.target.value })}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors cursor-pointer appearance-none"
                    >
                      {['Español', 'English', 'Português', 'Français'].map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

              {/* FRECUENCIA RFW */}
              <section className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-broadcast-line text-foreground-500 text-xs"></i>
                  Frecuencia RFW
                </h4>
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <div className="flex-1 w-full">
                    <label className="block text-foreground-500 text-[10px] font-medium mb-1">Identificador RFW</label>
                    <input
                      type="text"
                      value={config.frequencyRfw}
                      onChange={(e) => updateConfig({ frequencyRfw: e.target.value })}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs font-mono outline-none focus:border-foreground-300 transition-colors"
                      placeholder="102.5 RFW"
                    />
                  </div>
                  <div className="flex-1 w-full">
                    <label className="block text-foreground-500 text-[10px] font-medium mb-1">Frecuencia (número)</label>
                    <input
                      type="text"
                      value={config.frequencyNum}
                      onChange={(e) => updateConfig({ frequencyNum: e.target.value })}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs font-mono outline-none focus:border-foreground-300 transition-colors"
                      placeholder="102.5"
                    />
                  </div>
                </div>
                <p className="text-foreground-400 text-[10px] mt-2 flex items-center gap-1">
                  <i className="ri-information-line text-[10px]"></i>
                  Este identificador es único y aparece en la URL pública de tu radio.
                </p>
              </section>

              {/* WEB */}
              <section className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-global-line text-foreground-500 text-xs"></i>
                  Web
                </h4>
                <input
                  type="url"
                  value={config.webUrl}
                  onChange={(e) => updateConfig({ webUrl: e.target.value })}
                  placeholder="https://turadio.com"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
                />
                <p className="text-foreground-400 text-[10px] mt-1.5">Sitio web oficial de tu radio. Los oyentes pueden visitarlo desde tu perfil público.</p>
              </section>

              {/* REDES SOCIALES */}
              <section className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-share-line text-foreground-500 text-xs"></i>
                  Redes Sociales
                </h4>
                <div className="space-y-1.5">
                  {config.socials.map((social) => (
                    <div
                      key={social.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${social.active ? 'bg-background-50 border-background-200' : 'bg-background-100/50 border-background-100'}`}
                    >
                      <button
                        onClick={() => updateSocial(social.id, 'active', !social.active)}
                        className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${social.active ? 'bg-emerald-500' : 'bg-background-300'}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 bg-background-50 rounded-full shadow-sm transition-transform ${social.active ? 'translate-x-[16px]' : 'translate-x-0.5'}`}></span>
                      </button>
                      <div className="flex items-center gap-2.5 w-28 shrink-0">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${social.active ? 'bg-background-200' : 'bg-background-200/50'}`}>
                          <i className={`${social.icon} ${social.active ? 'text-foreground-600' : 'text-foreground-300'} text-sm`}></i>
                        </div>
                        <span className={`text-xs font-medium ${social.active ? 'text-foreground-800' : 'text-foreground-300'}`}>{social.platform}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <input
                          type="text"
                          value={social.url}
                          onChange={(e) => updateSocial(social.id, 'url', e.target.value)}
                          disabled={!social.active}
                          placeholder={`https://${social.id}.com/turadio`}
                          className={`w-full px-3 py-2 rounded-xl text-xs outline-none transition-colors ${social.active ? 'bg-background-50 border border-background-200 focus:border-foreground-300' : 'bg-background-100 border border-background-100 text-foreground-300 cursor-not-allowed'}`}
                        />
                      </div>
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${social.active && social.url ? 'bg-emerald-500' : 'bg-background-300'}`}></span>
                    </div>
                  ))}
                </div>
                <p className="text-foreground-400 text-[10px] mt-1.5">
                  {activeSocials.length} de {config.socials.length} redes activas con URL
                </p>
              </section>

              {/* CONTACTO */}
              <section className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-mail-line text-foreground-500 text-xs"></i>
                  Contacto
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Email</label>
                    <input
                      type="email"
                      value={config.email}
                      onChange={(e) => updateConfig({ email: e.target.value })}
                      placeholder="contacto@turadio.com"
                      className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Teléfono</label>
                      <input
                        type="text"
                        value={config.phone}
                        onChange={(e) => updateConfig({ phone: e.target.value })}
                        placeholder="+502 5555 1234"
                        className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-foreground-600 text-[10px] font-semibold mb-1">WhatsApp</label>
                      <input
                        type="text"
                        value={config.whatsapp}
                        onChange={(e) => updateConfig({ whatsapp: e.target.value })}
                        placeholder="+502 5555 1234"
                        className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Ciudad</label>
                      <input
                        type="text"
                        value={config.city}
                        onChange={(e) => updateConfig({ city: e.target.value })}
                        placeholder="Ciudad"
                        className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-foreground-600 text-[10px] font-semibold mb-1">País</label>
                      <input
                        type="text"
                        value={config.country}
                        onChange={(e) => updateConfig({ country: e.target.value })}
                        placeholder="País"
                        className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Dirección</label>
                    <input
                      type="text"
                      value={config.address}
                      onChange={(e) => updateConfig({ address: e.target.value })}
                      placeholder="Av. Central 456, Zona 1"
                      className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
                    />
                  </div>
                  <div className="p-3 rounded-xl bg-background-50 border border-background-200 space-y-2">
                    <p className="text-foreground-700 text-[10px] font-semibold">Visibilidad en perfil público</p>
                    {[
                      { field: 'showEmail' as const, label: 'Mostrar Email', icon: 'ri-mail-line' },
                      { field: 'showPhone' as const, label: 'Mostrar Teléfono', icon: 'ri-phone-line' },
                      { field: 'showWhatsapp' as const, label: 'Mostrar WhatsApp', icon: 'ri-whatsapp-line' },
                    ].map((item) => (
                      <div key={item.field} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <i className={`${item.icon} text-foreground-400 text-xs`}></i>
                          <span className="text-foreground-700 text-xs">{item.label}</span>
                        </div>
                        <button
                          onClick={() => updateConfig({ [item.field]: !config[item.field] })}
                          className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${config[item.field] ? 'bg-emerald-500' : 'bg-background-300'}`}
                        >
                          <span className={`absolute top-0.5 w-4 h-4 bg-background-50 rounded-full shadow-sm transition-transform ${config[item.field] ? 'translate-x-[16px]' : 'translate-x-0.5'}`}></span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Bottom save button */}
              <div className="pt-2 pb-8">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full lg:w-auto px-8 py-3 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>

              {/* ─── ADMINISTRATOR ACCOUNT ─── */}
              <section className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-user-settings-line text-foreground-500 text-xs"></i>
                  Cuenta del Administrador
                </h4>
                <p className="text-foreground-400 text-[10px] mb-4 leading-relaxed">
                  Datos personales y preferencias de acceso. Esta información no se muestra públicamente.
                </p>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Nombre completo</label>
                      <input
                        type="text"
                        defaultValue="Carlos Mendoza"
                        className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Email de acceso</label>
                      <input
                        type="email"
                        defaultValue="admin@radioactitud.com"
                        className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Rol</label>
                      <div className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs text-foreground-500">
                        Administrador
                      </div>
                    </div>
                    <div>
                      <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Idioma del panel</label>
                      <select
                        defaultValue="Español"
                        className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors cursor-pointer appearance-none"
                      >
                        {['Español', 'English', 'Português'].map((l) => (
                          <option key={l} value={l}>{l}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2.5">
                    <i className="ri-information-line text-amber-500 text-sm mt-0.5 shrink-0"></i>
                    <p className="text-amber-700 text-[10px] leading-relaxed">
                      Estos datos son solo para tu referencia. El acceso real al panel se gestiona desde <strong>Staff & Permisos</strong>. Para cambiar tu contraseña o método de acceso, usá la sección de seguridad cuando esté disponible.
                    </p>
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-5 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                </div>
              </section>
            </div>

            {/* ─── RIGHT: Vista Previa Pública ─── */}
            <div className="lg:w-[420px] shrink-0">
              <div className="lg:sticky lg:top-24">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-eye-line text-foreground-500 text-sm"></i>
                  <span className="text-foreground-700 text-xs font-semibold uppercase tracking-wider">Vista Previa Pública</span>
                </div>

                <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
                  {/* Cover */}
                  <div className="relative w-full h-40 bg-background-100">
                    {config.cover ? (
                      <img src={config.cover} alt="Cover preview" className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-foreground-200 text-3xl"></i>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20"></div>
                  </div>

                  {/* Logo + Name */}
                  <div className="px-5 -mt-10 relative z-10">
                    <div className="flex items-end gap-3">
                      <div className="w-20 h-20 rounded-2xl bg-background-50 border-2 border-background-50 shadow-sm overflow-hidden shrink-0 ring-2 ring-background-100">
                        {config.logo ? (
                          <img src={config.logo} alt="Logo preview" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-background-100">
                            <i className="ri-image-line text-foreground-300 text-xl"></i>
                          </div>
                        )}
                      </div>
                      <div className="pb-2">
                        <h3 className="text-foreground-950 text-base font-bold">{config.radioName || 'Radio Nueva Actitud'}</h3>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                          <span className="text-foreground-400 text-[10px]">Transmitiendo · {config.frequencyRfw}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="px-5 pt-4 pb-3">
                    <p className="text-foreground-600 text-xs leading-relaxed line-clamp-3">
                      {config.description || 'Sin descripción.'}
                    </p>
                  </div>

                  {/* Web */}
                  {config.webUrl && (
                    <div className="px-5 pb-3">
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-50 border border-background-100">
                        <i className="ri-global-line text-foreground-400 text-xs"></i>
                        <a href={config.webUrl} className="text-foreground-600 text-[11px] truncate hover:text-foreground-800 transition-colors">{config.webUrl}</a>
                      </div>
                    </div>
                  )}

                  {/* Social links */}
                  {activeSocials.length > 0 && (
                    <div className="px-5 pb-4">
                      <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-2">Redes Sociales</p>
                      <div className="flex flex-wrap gap-1.5">
                        {activeSocials.map((s) => (
                          <span
                            key={s.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-medium"
                          >
                            <i className={`${s.icon} text-[10px]`}></i>
                            {s.platform}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <hr className="border-background-100 mx-5" />

                  {/* Contact */}
                  <div className="px-5 py-4">
                    <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-2">Contacto</p>
                    <div className="space-y-2">
                      {config.showEmail && config.email && (
                        <div className="flex items-center gap-2 text-foreground-500 text-[11px]">
                          <i className="ri-mail-line text-xs text-foreground-300"></i>
                          <span>{config.email}</span>
                        </div>
                      )}
                      {config.showPhone && config.phone && (
                        <div className="flex items-center gap-2 text-foreground-500 text-[11px]">
                          <i className="ri-phone-line text-xs text-foreground-300"></i>
                          <span>{config.phone}</span>
                        </div>
                      )}
                      {config.showWhatsapp && config.whatsapp && (
                        <div className="flex items-center gap-2 text-foreground-500 text-[11px]">
                          <i className="ri-whatsapp-line text-xs text-foreground-300"></i>
                          <span>{config.whatsapp}</span>
                        </div>
                      )}
                      <div className="flex items-start gap-2 text-foreground-400 text-[11px]">
                        <i className="ri-map-pin-line text-xs text-foreground-300 mt-0.5"></i>
                        <span>{[config.address, config.city, config.country].filter(Boolean).join(', ') || 'Sin ubicación'}</span>
                      </div>
                    </div>
                    {!config.showEmail && !config.showPhone && !config.showWhatsapp && !config.address && (
                      <p className="text-foreground-300 text-[10px] italic">Sin datos de contacto visibles</p>
                    )}
                  </div>

                  <hr className="border-background-100 mx-5" />

                  {/* Preview note */}
                  <div className="px-5 py-3 bg-background-50">
                    <p className="text-foreground-400 text-[9px] flex items-center gap-1.5">
                      <i className="ri-information-line text-[9px]"></i>
                      Así ven tu perfil los oyentes en la página pública de tu radio.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>



    </>
  );
}